import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import styles from "./NotesSection.module.scss";
import Note from "./Note";
import MonthGroup from "./MonthGroup";
import {
  ChatCircleIcon,
  PaperclipIcon,
  PaperPlaneIcon,
  CaretDownIcon
} from "./icons";
import UndoIcon from '../../../../../../assets/svg/icons/common/Undo';
import FileIcon from '../../../../../../assets/svg/icons/common/FileIcon';
import CrossIcon from '../../../../../../assets/svg/icons/common/cross';
import { getNotesThankyou, addNoteThankyou, undoNoteThankyou } from "../../../../../../../api";
import useUserStore from "../../../../../../stores/user";
import useSalesFormStore from "../../../../../../stores/sales-form";

/**
 * NotesSection component
 * Displays a sidebar for adding and viewing notes related to an application
 * Features:
 * - Filtering notes by type (All, Internal, Agent, Merchant, Processor)
 * - Adding new notes with type selection
 * - Auto-expanding textarea
 * - Send on Enter (Shift+Enter for new line)
 */
const NotesSection = ({ appData }) => {
  const [noteText, setNoteText] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("Merchant");
  const [selectedNoteType, setSelectedNoteType] = useState("Merchant");
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [isNoteTypeDropdownOpen, setIsNoteTypeDropdownOpen] = useState(false);
  const [groupedNotes, setGroupedNotes] = useState([]);
  const [filteredGroupedNotes, setFilteredGroupedNotes] = useState([]);
  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  // Add state for reply
  const [replyTo, setReplyTo] = useState(null);
  // Add state for selected files
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});

  // store values
  const isPCAdmin = useUserStore(state => state.isPCAdmin);
  const user = useUserStore(state => state.user);
  const activeSigner = useSalesFormStore(state => state.activeSigner);
  console.log('activeSigner', activeSigner);

  // Define filters to match actual API signal values
  const filters = ["All", "PCI", "Agent", "Merchant"];

  // Signal to filter mapping
  const signalFilterMap = {
    "All": null,
    "PCI": "danger",
    "Agent": "warning",
    "Merchant": "merchant"
  };

  // Note types that can be selected when creating a note
  const noteTypes = ["Agent", "PCI", "Merchant"];
  const noteTypeMerchant = ["Merchant"];

  useEffect(() => {
    if (selectedFilter === "All") {
      setFilteredNotes(notes);
    } else {
      const signal = signalFilterMap[selectedFilter];
      if (signal) {
        let filteredNotes = notes.filter(note => note.signal === signal);
        setFilteredNotes(filteredNotes);
      }
    }
  }, [selectedFilter, notes]);

  // Apply filtering to the grouped notes
  useEffect(() => {
    if (!groupedNotes.length) {
      setFilteredGroupedNotes([]);
      return;
    }

    if (selectedFilter === "Merchant") {
      // No need to filter, just use the original groupedNotes
      setFilteredGroupedNotes(groupedNotes);
      return;
    }

    // Create a filtered version of the grouped notes
    const signal = signalFilterMap[selectedFilter];

    if (!signal) {
      setFilteredGroupedNotes(groupedNotes);
      return;
    }

    const filtered = groupedNotes.map(monthGroup => {
      const filteredThreads = monthGroup.threads.filter(thread => {
        // Use the type that was mapped from signal
        const noteType = thread.mainNote.type;
        return noteType === selectedFilter;
      });

      return {
        ...monthGroup,
        threads: filteredThreads
      };
    }).filter(group => group.threads.length > 0);

    setFilteredGroupedNotes(filtered);
  }, [selectedFilter, groupedNotes]);

  // Helper function to format date for month groups
  const formatMonthYear = (dateString) => {
    try {
      const date = new Date(dateString);
      // Format as "Month YYYY" to match Figma design
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch (e) {
      // Fallback in case of invalid date
      return 'Unknown Date';
    }
  };

  // Helper to group notes by month
  const groupNotesByMonth = (notesData) => {
    // Check if we have valid notes data
    if (!notesData || !notesData.data || !Array.isArray(notesData.data) || notesData.data.length === 0) {
      return [];
    }

    const notes = notesData.data;

    // Sort notes by date (newest first)
    const sortedNotes = [...notes].sort((a, b) => {
      // Handle missing date fields safely
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    // Create a map of month-year to notes
    const monthsMap = {};

    sortedNotes.forEach(note => {
      // Skip notes without created_at or id
      if (!note.created_at || !note.id) {
        return;
      }

      try {
        const monthYear = formatMonthYear(note.created_at);

        if (!monthsMap[monthYear]) {
          monthsMap[monthYear] = [];
        }

        // Check if this note has children (replies)
        // Allow for both null/undefined children and empty array
        const hasChildren = note.children && Array.isArray(note.children) && note.children.length > 0;

        // Create a thread with the main note
        const thread = {
          mainNote: {
            id: note.id,
            who: note.who || 'Unknown',
            recipient: true,
            // Map signal to type for display
            type: mapSignalToType(note.signal || 'warning'),
            created_at: note.created_at,
            content: note.content || '',
            files: note.files || []
          },
          replies: []
        };

        // Add any children as replies, with proper formatting
        if (hasChildren) {
          thread.replies = note.children.map(child => {
            // Map child.notes_type to signal
            let signal = child.signal;
            // if (child.signal === 'merchant_note') signal = 'merchant';
            // else if (child.signal === 'admin_note') signal = 'danger';
            // else if (child.signal === 'underwriting_note') signal = 'danger';

            // Format the child note similar to parent notes
            return {
              id: child.id || Math.random().toString(),
              // If child has user_id but no who field, try to determine the name
              who: child.who || (child.user_id ? (child.user_id === 1 ? 'Admin' : 'User ' + child.user_id) : 'Unknown'),
              recipient: true,
              created_at: child.created_at || new Date().toISOString(),
              // Generate created_ago if not available
              created_ago: child.created_ago || getTimeAgo(child.created_at),
              content: child.content || '',
              // Files may be null or an array
              files: child.files || [],
              // Map notes_type to display type
              type: mapSignalToType(signal)
            };
          });
        }

        monthsMap[monthYear].push(thread);
      } catch (error) {
        console.error('Error processing note:', error, note);
        // Continue with next note
      }
    });

    // If no valid notes were processed, return empty array
    if (Object.keys(monthsMap).length === 0) {
      return [];
    }

    // Convert map to array format expected by the component
    const groupedByMonth = Object.keys(monthsMap).map(month => ({
      month,
      threads: monthsMap[month]
    }));

    return groupedByMonth;
  };

  // Helper to get time ago string if not provided by API
  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);

      if (seconds < 60) return 'Just now';

      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;

      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;

      const days = Math.floor(hours / 24);
      if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;

      const months = Math.floor(days / 30);
      if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;

      const years = Math.floor(months / 12);
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    } catch (e) {
      return 'Recently';
    }
  };

  // Helper to map signal values to type names
  const mapSignalToType = (signal) => {
    if (!signal) return 'Agent'; // Default to Agent if no signal

    const signalMap = {
      'warning': 'Agent',
      'danger': 'PCI',
      'merchant': 'Merchant',
      'success': 'Processor'
    };

    return signalMap[signal] || 'Agent';
  };

  // Reverse mapping for when creating notes
  const mapTypeToSignal = (type) => {
    const typeMap = {
      'Agent': 'agent_note',
      'PCI': 'admin_note',
      'Merchant': 'merchant_note',
    };

    return typeMap[type] || 'warning';
  };

  const loadNotes = () => {
    if (!appData?.editslug) {
      console.warn('Cannot load notes: Missing editslug in appData');
      // toast.error('Cannot load notes: Missing editslug in appData');
      return;
    }

    setIsLoading(true);

    let data = {
      editslug: appData.editslug,
    };

    getNotesThankyou(data)
      .then((res) => {
        try {
          // Validate response structure
          if (!res.status) {
            console.error('Invalid API response format:', res);
            toast.error('Invalid API response format');
            setNotes([]);
            setFilteredNotes([]);
            setGroupedNotes([]);
            setFilteredGroupedNotes([]);
            return;
          }

          // Store the notes array from the response
          const notesArray = res.data && Array.isArray(res.data) ? res.data : [];
          setNotes(notesArray);

          // Filter notes based on selected filter
          if (selectedFilter === "All") {
            setFilteredNotes(notesArray);
          } else {
            const signal = signalFilterMap[selectedFilter];
            if (signal) {
              const filtered = notesArray.filter(note => note.signal === signal);
              setFilteredNotes(filtered);
            } else {
              setFilteredNotes(notesArray);
            }
          }

          // Process the notes into grouped format
          const grouped = groupNotesByMonth(res);
          setGroupedNotes(grouped);

          // Initialize filtered grouped notes based on current filter
          if (selectedFilter === "All") {
            setFilteredGroupedNotes(grouped);
          } else {
            const signal = signalFilterMap[selectedFilter];
            if (signal) {
              const filtered = grouped.map(monthGroup => {
                const filteredThreads = monthGroup.threads.filter(thread => {
                  return thread.mainNote.type === selectedFilter;
                });

                return {
                  ...monthGroup,
                  threads: filteredThreads
                };
              }).filter(group => group.threads.length > 0);

              setFilteredGroupedNotes(filtered);
            } else {
              setFilteredGroupedNotes(grouped);
            }
          }
        } catch (error) {
          console.error('Error processing notes response:', error);
          toast.error('Error processing notes data');
          setNotes([]);
          setFilteredNotes([]);
          setGroupedNotes([]);
          setFilteredGroupedNotes([]);
        }
      })
      .catch((err) => {
        console.error('Error loading notes:', err);
        toast.error(err.message || 'Failed to load notes');
        setNotes([]);
        setFilteredNotes([]);
        setGroupedNotes([]);
        setFilteredGroupedNotes([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsNoteTypeDropdownOpen(false);
      }
    };

    loadNotes();
    if (!user) {
      setSelectedNoteType("Merchant");
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [appData]);

  const handleNoteTypeChange = (type) => {
    setSelectedNoteType(type);
    setIsNoteTypeDropdownOpen(false);
  };

  const undoClick = (response, submit_data, toastId) => {
    undoNote({
      note_id: response.notes.id,
    })
      .then((res) => {
        if (res && (res.status === true || res.status === 200)) {
          const message = res.message || "Note has been removed successfully";
          toast.success(message, {
            id: toastId,
            duration: 10 * 1000,
          });
          loadNotes();
          setNoteText(submit_data.content);
        } else {
          throw new Error((res && res.message) || 'Failed to undo note');
        }
      })
      .catch((err) => {
        console.error('Error undoing note:', err);
        toast.error(err.message || "Failed to remove note", {
          id: toastId,
          duration: 10 * 1000,
        });
      });
  };

  const handleTextareaChange = (e) => {
    setNoteText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
      }
    }
  };

  // Close reply UI and reset state
  const cancelReply = () => {
    setReplyTo(null);
  };

  // Handle replying to a thread
  const handleThreadReply = (thread) => {
    // Set the replyTo state with thread information
    setReplyTo(thread);

    // Focus the textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];

    // Validate file types
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Only PDF, Excel, Word and Image files are allowed');
      e.target.value = '';
      return;
    }

    // Calculate remaining slots
    const remainingSlots = 3 - selectedFiles.length;
    if (remainingSlots <= 0) {
      toast.error('Maximum 3 files allowed');
      e.target.value = '';
      return;
    }

    // Take only the first N files that would fit within the 3-file limit
    const filesToAdd = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more file${remainingSlots > 1 ? 's' : ''} can be added`);
    }

    // Add files to state with additional metadata
    const filesWithMetadata = filesToAdd.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    }));

    setSelectedFiles(prevFiles => [...prevFiles, ...filesWithMetadata]);
    e.target.value = '';
  };

  // Remove file from selection
  const removeFile = (idToRemove) => {
    setSelectedFiles(prevFiles => prevFiles.filter(file => file.id !== idToRemove));
  };

  // Modified onSend to handle both normal notes and replies with file attachments
  const onSend = async () => {
    // Validate inputs
    if (selectedFiles.length === 0 && !noteText.trim()) {
      toast.error('Please add a note or attach files');
      return;
    }

    // Validate app data
    if (!appData?.editslug) {
      toast.error('Cannot add note: Missing application data');
      console.error('Missing editslug in appData', appData);
      return;
    }

    // Store parent ID before resetting replyTo
    const parentNote = replyTo?.mainNote;
    const isReply = Boolean(parentNote?.id);
    const parentNoteId = parentNote?.id;

    setIsUploading(true);
    const toastId = toast.loading(isReply ? "Adding reply..." : "Adding note...", { duration: 30 * 1000 });

    try {
      // Prepare form data for API call
      const formData = new FormData();
      formData.append('editslug', appData.editslug);
      formData.append('content', noteText);

      // Map the selected note type to the appropriate note type
      const noteType = mapTypeToSignal(selectedNoteType);
      formData.append('notes_type', noteType);

      if (isReply) {
        console.log('Adding reply to note ID:', parentNoteId);
        formData.append('parent_id', parentNoteId);
      } else {
        formData.append('parent_id', '');
      }

      // Add files if any
      if (selectedFiles.length > 0) {
        selectedFiles.forEach((fileData, index) => {
          if (fileData && fileData.file) {
            formData.append(`files[]`, fileData.file);
          }
        });
      }

      // Add activeSigner to formData
      if (activeSigner) {
        formData.append('signer_id', activeSigner.id);
      }

      // Call the API to add the note
      const response = await addNoteThankyou(formData);

      // Validate response - success can be either status === true or status === 200
      if (response && (response.status === true || response.status === 200)) {
        toast.success(isReply ? "Reply added successfully" : "Note added successfully", {
          id: toastId,
          duration: 5000,
        });

        // Reset states
        setNoteText("");
        setReplyTo(null);
        setSelectedFiles([]);

        // If we're adding a reply, manually update the local state
        if (isReply && response.notes) {
          // Create a temporary reply object using the response data
          const newReply = {
            id: response.notes.id,
            who: user?.name || 'You',
            recipient: true,
            created_at: response.notes.created_at || new Date().toISOString(),
            created_ago: 'Just now',
            content: response.notes.content || noteText,
            files: response.notes.files || [],
            type: selectedNoteType
          };

          // Find the thread we're replying to in the grouped notes
          const updatedGroupedNotes = groupedNotes.map(monthGroup => {
            const updatedThreads = monthGroup.threads.map(thread => {
              // If this is the thread we're replying to, add the new reply
              if (thread.mainNote.id === parentNoteId) {
                return {
                  ...thread,
                  replies: [...thread.replies, newReply]
                };
              }
              return thread;
            });

            return {
              ...monthGroup,
              threads: updatedThreads
            };
          });

          // Update the local state with our modified notes
          setGroupedNotes(updatedGroupedNotes);

          // Also update filtered grouped notes if needed
          if (selectedFilter === "All" ||
            (parentNote.type === selectedFilter)) {
            setFilteredGroupedNotes(updatedGroupedNotes);
          }
        }

        // Refresh notes list from server (always do this to keep in sync)
        loadNotes();
      } else {
        throw new Error((response && response.message) || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(error.message || "Something went wrong!", {
        duration: 10 * 1000,
        id: toastId
      });
    } finally {
      setIsUploading(false);
    }
  };

  const toggleReplies = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  return (
    <div className={styles.notesContainer}>
      <div className={styles.header}>
        <h4 className={styles.title}>Notes</h4>
        {/* <div className={styles.tabContainer}>
          <div className={styles.tabList}>
            {filters.map((filter) => (
              <button
                key={filter}
                className={`${styles.tab} ${selectedFilter === filter ? styles.activeTab : ""}`}
                onClick={() => setSelectedFilter(filter)}
                title={filter}
              >
                {filter}
              </button>
            ))}
          </div>
        </div> */}
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <p>Loading notes...</p>
          </div>
        ) : filteredGroupedNotes.length > 0 ? (
          <div className={styles.notesList}>
            {filteredGroupedNotes.map((monthGroup, monthIndex) => (
              <MonthGroup
                key={monthIndex}
                title={monthGroup.month}
                threads={monthGroup.threads.map(thread => ({
                  ...thread,
                  content: (
                    <>
                      {thread.mainNote && (
                        <>
                          <Note
                            key={thread.mainNote.id}
                            note={thread.mainNote}
                            onReplyClick={() => handleThreadReply(thread)}
                          />

                          {thread.replies && Array.isArray(thread.replies) && thread.replies.length > 0 && (
                            <div className={`${styles.replies} ${thread.replies && Array.isArray(thread.replies) && thread.replies.length > 0 ? styles.expanded : ""}`}>
                              {/* First reply is always visible */}
                              <Note
                                key={thread.replies[0].id}
                                note={thread.replies[0]}
                                isReply={true}
                                showReplyButton={false}
                              />

                              {/* Additional replies when expanded */}
                              {expandedNotes[thread.mainNote.id] && thread.replies.length > 1 && (
                                <div className={styles.additionalReplies}>
                                  {thread.replies.slice(1).map((reply, index) => (
                                    <Note
                                      key={reply.id || `reply-${index + 1}`}
                                      note={reply}
                                      isReply={true}
                                      showReplyButton={false}
                                    />
                                  ))}
                                </div>
                              )}

                              {/* Show more replies button if there are additional replies */}
                              {thread.replies.length > 1 && (
                                <div className={styles.showMoreReplies}>
                                  <button
                                    className={styles.showMoreRepliesButton}
                                    onClick={() => toggleReplies(thread.mainNote.id)}
                                    data-expanded={expandedNotes[thread.mainNote.id] ? "true" : "false"}
                                  >
                                    {expandedNotes[thread.mainNote.id] ? 'Hide replies' : `Show ${thread.replies.length - 1} more ${thread.replies.length - 1 === 1 ? 'reply' : 'replies'}`}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )
                }))}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.iconContainer}>
              <ChatCircleIcon />
            </div>
            <p className={styles.emptyMessage}>
              You can leave any additional notes for your team, agent, merchant or processor here
            </p>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {replyTo && (
          <div className={styles.replyPreview}>
            <div className={styles.replyPreviewHeader}>
              <span className={styles.replyToText}>Reply to {replyTo.mainNote.who}</span>
              <button
                className={styles.closeReplyButton}
                onClick={cancelReply}
                title="Cancel reply"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5" stroke="#3A383F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className={styles.replyPreviewContent}>
              {replyTo.mainNote.content}
            </div>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className={styles.selectedFiles}>
            {selectedFiles.map((fileData) => (
              <div
                key={fileData.id}
                className={styles.fileItem}
                title={fileData.name}
              >
                <FileIcon color="#E03B24" />
                <div className={styles.fileInfo}>
                  <span className={styles.fileName}>{fileData.name}</span>
                </div>
                <button
                  className={styles.removeFileButton}
                  onClick={() => removeFile(fileData.id)}
                  disabled={isUploading}
                >
                  <CrossIcon />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          type="file"
          className={styles.fileInput}
          id="file-input"
          ref={fileInputRef}
          multiple
          accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,.gif"
          onChange={handleFileUpload}
          disabled={isUploading}
        />

        <div className={styles.textareaContainer}>
          <textarea
            ref={textareaRef}
            placeholder={replyTo ? "Write a reply..." : "Add a note"}
            value={noteText}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            className={styles.textarea}
            rows={1}
            disabled={isUploading}
          />
        </div>

        <button
          className={styles.attachButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || selectedFiles.length >= 3}
        >
          <PaperclipIcon />
        </button>

        {/* {!replyTo && (
          <div className={styles.dropdownContainer} ref={dropdownRef}>
            <button
              className={styles.dropdownToggle}
              onClick={() => setIsNoteTypeDropdownOpen(!isNoteTypeDropdownOpen)}
              disabled={!user || isUploading}
            >
              <span className={styles.dropdownLabel}>{selectedNoteType}</span>
              <CaretDownIcon />
            </button>

            {isNoteTypeDropdownOpen && (
              <div className={styles.dropdownMenu}>
                {isPCAdmin ? noteTypes.map(type => (
                  <button
                    key={type}
                    className={`${styles.dropdownItem} ${selectedNoteType === type ? styles.active : ''}`}
                    onClick={() => handleNoteTypeChange(type)}
                  >
                    {type}
                  </button>
                )) : noteTypeMerchant.map(type => (
                  <button
                    key={type}
                    className={`${styles.dropdownItem} ${selectedNoteType === type ? styles.active : ''}`}
                    onClick={() => handleNoteTypeChange(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
        )} */}

        <button
          className={styles.sendButton}
          onClick={onSend}
          disabled={(!noteText.trim() && selectedFiles.length === 0) || isUploading}
        >
          <PaperPlaneIcon />
        </button>
      </div>
    </div>
  );
};

NotesSection.propTypes = {
  appData: PropTypes.object
};

export default NotesSection; 